// frontend/src/components/FastReact/PrepPickup.jsx
// Web portal version of the container pickup registration.
// Uses PlanoTeares (embedded + readOnly) to show all READY orders below the input.

import React, { useState, useRef } from 'react';
import { FiCheckCircle, FiPackage, FiRotateCcw } from 'react-icons/fi';
import { fastReactAPI } from '../../api/client';
import PlanoTeares from './PlanoTeares';
import Toast    from '../Common/Toast';
import { useToast } from '../../hooks/useToast';

// Filters pre-set for embedded view: only READY orders, all print statuses
const EMBEDDED_FILTERS = { status: 'READY', print_status: 'false' };

const PrepPickup = () => {
  const { toasts, removeToast, toast_success, toast_error, toast_warning } = useToast();
  const [input,   setInput]   = useState('');
  const [loading, setLoading] = useState(false);
  const [result,  setResult]  = useState(null);
  const inputRef = useRef(null);

  const doPickup = async (e) => {
    e?.preventDefault();
    const code = input.trim();
    if (!code) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await fastReactAPI.pickupContainer(code);
      setResult(r.data);
      setInput('');
      if (r.data.already_completed)
        toast_warning(`Ordem ${r.data.po_setup?.po_code_line_full} já estava recolhida`);
      else
        toast_success(`Recolha de ${r.data.po_setup?.po_code_line_full} registada`);
    } catch (err) {
      toast_error(err.response?.data?.error || 'Erro ao processar recolha');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setInput('');
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  return (
    <div className="plano-teares-list">
      <div className="page-header page-header--compact">
        <h2>Recolha de Carrinho</h2>
      </div>

      {/* ── Pickup input form ─────────────────────────────────────────────── */}
      <div style={{ maxWidth: 560, marginBottom: 32 }}>

        {/* Success */}
        {result?.success && (
          <div style={{ background: result.already_completed ? '#fef3c7' : '#dcfce7',
                        border: `1px solid ${result.already_completed ? '#fcd34d' : '#86efac'}`,
                        borderRadius:10, padding:20, marginBottom:20 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
              <FiCheckCircle size={20} style={{ color: result.already_completed ? '#92400e' : '#166534' }} />
              <span style={{ fontWeight:700, fontSize:15,
                             color: result.already_completed ? '#92400e' : '#166534' }}>
                {result.already_completed
                  ? 'Esta ordem já estava recolhida'
                  : 'Recolha registada com sucesso'}
              </span>
            </div>
            <table style={{ fontSize:13, borderCollapse:'collapse', width:'100%' }}>
              <tbody>
                {[
                  ['Ordem',    result.po_setup?.po_code_line_full],
                  ['Máquina',  result.po_setup?.machine],
                  ['Carrinho', result.po_setup?.container_material],
                  ['Produto',  result.po_setup?.product_name],
                  ['Cliente',  result.po_setup?.customer_name],
                  ['Progresso',`${result.prepared_count}/${result.total_count} itens concluídos`],
                ].filter(([,v]) => v).map(([label, value]) => (
                  <tr key={label}>
                    <td style={{ padding:'3px 12px 3px 0', fontWeight:600,
                                 color: result.already_completed ? '#92400e' : '#166534',
                                 width:90, verticalAlign:'top' }}>
                      {label}
                    </td>
                    <td style={{ padding:'3px 0',
                                 color: result.already_completed ? '#92400e' : '#166534' }}>
                      {value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button type="button" className="btn btn-secondary btn-sm"
              style={{ marginTop:14 }} onClick={reset}>
              <FiRotateCcw size={13} style={{ marginRight:5 }} />
              Nova Recolha
            </button>
          </div>
        )}



        {/* Input form */}
        {!result?.success && (
          <div className="table-container" style={{ padding:24 }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:16 }}>
              <FiPackage size={20} style={{ color:'var(--primary)' }} />
              <div>
                <div style={{ fontWeight:700, fontSize:15 }}>Confirmar Recolha de Materiais</div>
                <div style={{ fontSize:12, color:'var(--gray-500)', marginTop:2 }}>
                  Introduza o código da ordem ou leia o QR Code da etiqueta.
                </div>
              </div>
            </div>

            <form onSubmit={doPickup} style={{ display:'flex', gap:8, alignItems:'flex-end' }}>
              <div style={{ flex:1 }}>
                <label style={{ display:'block', fontSize:12, fontWeight:600,
                                 color:'var(--gray-700)', marginBottom:4,
                                 textTransform:'uppercase', letterSpacing:'0.04em' }}>
                  Código da Ordem
                </label>
                <input
                  ref={inputRef}
                  type="text"
                  className="form-control"
                  style={{ fontFamily:'monospace', fontWeight:700, fontSize:15,
                           textTransform:'uppercase', letterSpacing:'0.5px' }}
                  placeholder="TC22600086-20002"
                  value={input}
                  onChange={e => setInput(e.target.value.toUpperCase())}
                  disabled={loading}
                  autoFocus
                  autoComplete="off"
                />
              </div>
              <button type="submit" className="btn btn-primary"
                disabled={loading || !input.trim()}
                style={{ whiteSpace:'nowrap', height:38 }}>
                {loading ? 'A processar…' : 'Confirmar Recolha'}
              </button>
            </form>

          </div>
        )}
      </div>

      {/* ── Embedded PlanoTeares — all READY orders, read-only ───────────── */}
      <div>

        {/* embedded hides the page header; readOnly disables advance/revert/print/urgent */}
        <PlanoTeares
          embedded
          readOnly
          hideContainerColumn
          initialFilters={EMBEDDED_FILTERS}
        />

      </div>
      {/* Toast notifications */}
      <div className="toast-container">
        {toasts.map(toast => (
          <Toast key={toast.id} id={toast.id} message={toast.message}
            type={toast.type} duration={toast.duration}
            requireConfirm={toast.requireConfirm} onConfirm={toast.onConfirm}
            onClose={removeToast} />
        ))}
      </div>
    </div>
  );
};

export default PrepPickup;